import React from 'react';
import { formatMoney, useCurrency } from '../context/CurrencyContext';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  MapPin, Clock, Users, Check, ChevronRight,
  Loader2, BanIcon, Calendar, Star, Send, Award
} from 'lucide-react';
import { toast } from 'sonner';
import VideoGallery from '../components/VideoGallery';
import VideoUpload from '../components/VideoUpload';
import { itemLabel } from '../lib/activityItems';

var API_URL = process.env.REACT_APP_BACKEND_URL;

function formatYen(amount, cur) {
  return formatMoney(amount, cur);
}

// Normalise la langue courante vers nos clés internes (fr / en / jp).
function langKey(l) {
  l = (l || 'en').toLowerCase();
  if (l.indexOf('ja') === 0 || l.indexOf('jp') === 0) return 'jp';
  if (l.indexOf('fr') === 0) return 'fr';
  return 'en';
}

function dateLocale(l) {
  var k = langKey(l);
  return k === 'jp' ? 'ja-JP' : k === 'fr' ? 'fr-FR' : 'en-US';
}

export default function ActivityDetail() {
  useCurrency(); // re-render quand la devise d'affichage change
  var { t, i18n } = useTranslation();
  var params = useParams();
  var activityId = params.activityId;
  var navigate = useNavigate();
  var auth = useAuth();
  var [activity, setActivity] = React.useState(null);
  var [loading, setLoading] = React.useState(true);
  var [reviews, setReviews] = React.useState([]);
  var [avgRating, setAvgRating] = React.useState(0);
  var [totalReviews, setTotalReviews] = React.useState(0);
  var [reviewableBooking, setReviewableBooking] = React.useState(null);
  var [myRating, setMyRating] = React.useState(0);
  var [myComment, setMyComment] = React.useState('');
  var [submittingReview, setSubmittingReview] = React.useState(false);
  var [actBadges, setActBadges] = React.useState([]);

  React.useEffect(function() {
    axios.get(API_URL + '/api/activities/' + activityId + '/badges')
      .then(function(res) { setActBadges((res.data && res.data.badges) || []); })
      .catch(function() { setActBadges([]); });
  }, [activityId]);

  function loadReviews() {
    axios.get(API_URL + '/api/activities/' + activityId + '/reviews')
      .then(function(res) {
        setReviews(res.data.reviews || []);
        setAvgRating(res.data.average_rating || 0);
        setTotalReviews(res.data.total_reviews || 0);
      })
      .catch(function() {});
  }

  React.useEffect(function() {
    axios.get(API_URL + '/api/activities/' + activityId)
      .then(function(res) {
        setActivity(res.data);
        setLoading(false);
      })
      .catch(function() {
        toast.error(t('activityDetail.notFound'));
        navigate('/activities');
      });
    loadReviews();
  }, [activityId, navigate]);

  React.useEffect(function() {
    if (!auth.user) { setReviewableBooking(null); return; }
    axios.get(API_URL + '/api/bookings/v2/me/reviewable', { withCredentials: true })
      .then(function(res) {
        var match = null;
        for (var i = 0; i < res.data.length; i++) {
          if (res.data[i].activity_id === activityId) { match = res.data[i]; break; }
        }
        setReviewableBooking(match);
      })
      .catch(function() {});
  }, [activityId, auth.user]);

  function submitReview() {
    if (myRating < 1) { toast.error(t('activityDetail.chooseRating')); return; }
    setSubmittingReview(true);
    axios.post(API_URL + '/api/activities/' + activityId + '/reviews', {
      booking_id: reviewableBooking.booking_id,
      rating: myRating,
      comment: myComment
    }, { withCredentials: true })
      .then(function() {
        toast.success(t('activityDetail.reviewThanks'));
        setReviewableBooking(null);
        setMyRating(0);
        setMyComment('');
        setSubmittingReview(false);
        loadReviews();
      })
      .catch(function(err) {
        setSubmittingReview(false);
        toast.error(err.response && err.response.data ? err.response.data.detail : t('activityDetail.error'));
      });
  }

  function handleBook() {
    if (!auth.user) {
      sessionStorage.setItem('returnAfterLogin', '/booking-activity/' + activityId);
      toast.info(t('activityDetail.loginToBook'));
      navigate('/login');
      return;
    }
    if (auth.user.role === 'guide') {
      toast.info(t('activityDetail.hostCannotBook'));
      return;
    }
    navigate('/booking-activity/' + activityId);
  }

  function handleDuplicate() {
    if (!auth.user) {
      sessionStorage.setItem('returnAfterLogin', '/activities/' + activityId);
      navigate('/login');
      return;
    }
    axios.post(API_URL + '/api/guides/me/activities/duplicate-from/' + activityId, {}, { withCredentials: true })
      .then(function() {
        toast.success(t('activityDetail.duplicated'));
        navigate('/guide/dashboard');
      })
      .catch(function(err) {
        var msg = err.response && err.response.data && err.response.data.detail;
        toast.error(msg || t('activityDetail.duplicateError'));
      });
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center pt-20">
        <Loader2 className="h-12 w-12 text-[#F9004D] animate-spin" />
      </div>
    );
  }

  if (!activity) return null;

  // Point de rendez-vous : si le guide a fourni des coordonnées exactes (via lien
  // Google Maps), on les utilise pour la carte/itinéraire ; sinon on retombe sur
  // le texte libre (moins précis).
  var hasMeetingCoords = activity.meeting_point_lat != null && activity.meeting_point_lng != null;
  var meetingQuery = hasMeetingCoords
    ? (activity.meeting_point_lat + ',' + activity.meeting_point_lng)
    : (activity.meeting_point || '');

  var avail = activity.availability || {};
  var daysArr = avail.days_of_week || [];
  var slotsArr = avail.time_slots || [];
  
  var daysLabels = [];
  for (var i = 0; i < daysArr.length; i++) {
    daysLabels.push(t('hostDash.days.' + daysArr[i], daysArr[i]));
  }
  var daysLabel = daysLabels.join(', ');

  var slotsLabels = [];
  for (var j = 0; j < slotsArr.length; j++) {
    slotsLabels.push(t('hostDash.slots.' + slotsArr[j], slotsArr[j]));
  }
  var slotsLabel = slotsLabels.join(' • ');

  var includedItems = [];
  if (activity.included) {
    for (var k = 0; k < activity.included.length; k++) {
      includedItems.push(
        <li key={k} className="flex items-center gap-2 text-green-400">
          <Check className="h-4 w-4" />
          <span>{itemLabel(t, activity.included[k])}</span>
        </li>
      );
    }
  }

  var notIncludedItems = [];
  if (activity.not_included) {
    for (var m = 0; m < activity.not_included.length; m++) {
      notIncludedItems.push(
        <li key={m} className="text-zinc-400">• {itemLabel(t, activity.not_included[m])}</li>
      );
    }
  }

  // Titre / description dans la langue du visiteur (repli si non renseigné).
  function pick(obj) {
    if (!obj) return '';
    if (typeof obj === 'string') return obj;
    var lk = langKey(i18n.language);
    return obj[lk] || obj.fr || obj.en || obj.jp || '';
  }
  var activityName = pick(activity.name);
  var activityDesc = pick(activity.description);

  var isSej = activity.activity_type === 'sejour';
  var pkg = activity.package_prices || {};
  var minP = parseInt(activity.min_participants, 10) || 1;
  var maxP = parseInt(activity.max_participants, 10) || 99;
  // La grille se lit par PALIERS (« jusqu'a N personnes ») : le tarif d'un
  // palier s'applique a tous les groupes plus petits non couverts par un palier
  // inferieur. On affiche donc des fourchettes (« 1 a 4 pers. »), sinon le prix
  // annonce ne correspondrait pas au prix facture.
  var pkgSizes = Object.keys(pkg).map(function(x) { return parseInt(x, 10); }).filter(function(x) { return x > 0 && x <= maxP && pkg[String(x)] > 0; }).sort(function(a, b) { return a - b; });
  // Deux paliers voisins au MEME prix sont fusionnes : afficher « 1 pers. »,
  // « 2 pers. », « 3 pers. », « 4 pers. » tous a 380 000 est du bruit — le
  // voyageur veut lire « 1 a 4 pers. : 380 000 ». La fusion se fait ici, a
  // l'affichage, pour rester vraie quelle que soit la facon dont le guide a
  // rempli sa grille (lignes vides ou montants repetes).
  var pkgBrackets = [];
  var bas = minP;
  pkgSizes.forEach(function(n) {
    if (n < bas) return;                      // palier entierement sous le minimum
    var prix = parseInt(pkg[String(n)], 10) || 0;
    var dernier = pkgBrackets[pkgBrackets.length - 1];
    if (dernier && dernier.price === prix) {
      dernier.to = n;                         // meme tarif : on etend la fourchette
    } else {
      pkgBrackets.push({ from: bas, to: n, price: prix });
    }
    bas = n + 1;
  });
  var destLabel = Array.isArray(activity.destinations) ? activity.destinations.join(' • ') : '';
  var itinerary = Array.isArray(activity.itinerary) ? activity.itinerary : [];

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="relative h-64 sm:h-96 rounded-2xl overflow-hidden bg-gradient-to-br from-[#F9004D]/20 to-[#00F0FF]/20 mb-6">
              {activity.photos && activity.photos.length > 0 ? (
                <img src={activity.photos[0]} alt={activity.location} className="absolute inset-0 w-full h-full object-cover"
                  onError={function(e) { e.target.style.display='none'; }} />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <MapPin className="h-24 w-24 text-white/30" />
                </div>
              )}
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="px-3 py-1 bg-[#F9004D] rounded-full text-sm text-white">
                    {activity.location}
                  </span>
                  {activity.meeting_point && (
                    <a href={'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(meetingQuery)}
                      target="_blank" rel="noreferrer"
                      className="px-3 py-1 bg-[#00F0FF]/20 border border-[#00F0FF]/40 rounded-full text-sm text-[#00F0FF] flex items-center gap-1 hover:bg-[#00F0FF]/30"
                      data-testid="activity-meeting-point">
                      <MapPin className="h-3 w-3" /> {activity.meeting_point}
                    </a>
                  )}
                  {totalReviews > 0 && (
                    <span className="px-3 py-1 bg-black/60 backdrop-blur-md rounded-full text-sm text-white flex items-center gap-1">
                      <Star className="h-3 w-3 fill-[#D4AF37] text-[#D4AF37]" />
                      {Number(avgRating).toFixed(1)} ({totalReviews} {t('activityDetail.reviewsWord')})
                    </span>
                  )}
                  {!isSej && activity.start_time && (
                    <span className="px-3 py-1 bg-black/60 backdrop-blur-md rounded-full text-sm text-[#00F0FF] flex items-center gap-1">
                      <Clock className="h-3 w-3" /> {activity.start_time}{activity.duration_hours ? ' · ' + activity.duration_hours + 'h' : ''}
                    </span>
                  )}
                  {!activity.children_allowed && (
                    <span className="px-3 py-1 bg-red-500 rounded-full text-xs text-white flex items-center gap-1">
                      <BanIcon className="h-3 w-3" /> {t('activityDetail.under18')}
                    </span>
                  )}
                </div>
                <h1 className="text-3xl sm:text-4xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
                  {activityName}
                </h1>
              </div>
            </div>

            <Card className="bg-zinc-900/50 border-white/10 p-6 mb-6">
              <div className="flex items-center gap-4 mb-4">
                <img
                  src={activity.guide_picture || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect width='100' height='100' fill='%2327272a'/%3E%3Ccircle cx='50' cy='40' r='16' fill='%2352525b'/%3E%3Cpath d='M22 82c0-15 12-24 28-24s28 9 28 24' fill='%2352525b'/%3E%3C/svg%3E"}
                  alt={activity.guide_name}
                  className="w-16 h-16 rounded-full object-cover object-top border-2 border-[#F9004D]"
                />
                <div>
                  <p className="text-zinc-400 text-sm">{t('activityDetail.yourHost')}</p>
                  <h3 className="text-xl font-bold text-white">{activity.guide_name}</h3>
                </div>
              </div>
              {activity.guide_language_levels && Object.keys(activity.guide_language_levels).some(k => activity.guide_language_levels[k] > 0) && (
                <div className="border-t border-white/10 pt-4 space-y-2">
                  <p className="text-zinc-400 text-xs mb-2">{t('activityDetail.spokenLanguages')}</p>
                  {[
                    { key: 'en', label: t('activityDetail.langEn') },
                    { key: 'fr', label: t('activityDetail.langFr') },
                    { key: 'jp', label: t('activityDetail.langJp') }
                  ].filter(({ key }) => activity.guide_language_levels[key] > 0).map(({ key, label }) => (
                    <div key={key} className="flex items-center gap-3">
                      <span className="text-zinc-300 text-sm w-28">{label}</span>
                      <div className="flex gap-1">
                        {[1,2,3,4,5,6,7].map(n => (
                          <div key={n} className={'w-3 h-3 rounded-full ' + (activity.guide_language_levels[key] >= n ? 'bg-[#F9004D]' : 'bg-white/10')} />
                        ))}
                      </div>
                      <span className="text-zinc-500 text-xs">{activity.guide_language_levels[key]}/7</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card className="bg-zinc-900/50 border-white/10 p-6 mb-6">
              <h2 className="text-xl font-bold text-white mb-4">{t('activityDetail.description')}</h2>
              <p className="text-zinc-300 leading-relaxed">
                {activityDesc}
              </p>
            </Card>

            <Card className="bg-zinc-900/50 border-white/10 p-6 mb-6">
              <h2 className="text-xl font-bold text-white mb-4">
                <Calendar className="inline h-5 w-5 mr-2 text-[#F9004D]" />
                {t('activityDetail.availability')}
              </h2>
              {isSej ? (
                <div className="space-y-3">
                  <div>
                    <p className="text-zinc-400 text-sm">{t('activityDetail.destinations')}</p>
                    <p className="text-white">{destLabel || activity.location}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">{t('activityDetail.duration')}</p>
                    <p className="text-white">{activity.duration_days ? t('activityDetail.daysCount', { count: activity.duration_days }) : t('activityDetail.notDefined')}</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <p className="text-zinc-400 text-sm">{t('activityDetail.days')}</p>
                    <p className="text-white">{daysLabel || t('activityDetail.notDefined')}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">{t('activityDetail.schedule')}</p>
                    <p className="text-white">{activity.start_time || slotsLabel || t('activityDetail.notDefined')}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">{t('activityDetail.duration')}</p>
                    <p className="text-white">{activity.duration_hours ? t('activityDetail.hours', { count: activity.duration_hours }) : t('activityDetail.notDefined')}</p>
                  </div>
                </div>
              )}
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {includedItems.length > 0 && (
                <Card className="bg-zinc-900/50 border-white/10 p-6">
                  <h3 className="text-lg font-bold text-white mb-4">{t('activityDetail.included')}</h3>
                  <ul className="space-y-2">{includedItems}</ul>
                </Card>
              )}
              {notIncludedItems.length > 0 && (
                <Card className="bg-zinc-900/50 border-white/10 p-6">
                  <h3 className="text-lg font-bold text-white mb-4">{t('activityDetail.notIncluded')}</h3>
                  <ul className="space-y-2">{notIncludedItems}</ul>
                </Card>
              )}
            </div>

            {isSej && itinerary.length > 0 && (
              <Card className="bg-zinc-900/50 border-white/10 p-6 mt-6" data-testid="sejour-itinerary">
                <h2 className="text-xl font-bold text-white mb-4">
                  <MapPin className="inline h-5 w-5 mr-2 text-[#F9004D]" />
                  {t('activityDetail.itineraryTitle')}
                </h2>
                <div className="space-y-4">
                  {itinerary.map(function(d) {
                    var dtitle = pick(d.title);
                    var ddesc = pick(d.description);
                    return (
                      <div key={d.day} className="border-l-2 border-[#F9004D]/50 pl-4">
                        <p className="text-[#F9004D] font-bold text-sm">{t('activityDetail.dayN', { day: d.day })}{d.location ? ' · ' + d.location : ''}</p>
                        {dtitle && <p className="text-white font-semibold">{dtitle}</p>}
                        {ddesc && <p className="text-zinc-400 text-sm mt-1">{ddesc}</p>}
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            {actBadges.length > 0 && (
              <Card className="bg-zinc-900/50 border-white/10 p-6 mt-6" data-testid="activity-badges">
                <h2 className="text-xl font-bold text-white mb-1">
                  <Award className="inline h-5 w-5 mr-2 text-[#00F0FF]" />
                  {t('activityDetail.badgesTitle')}
                </h2>
                <p className="text-zinc-400 text-sm mb-4">{t('activityDetail.badgesHint')}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {actBadges.map(function(b) {
                    return (
                      <div key={b.id} className="flex items-center gap-3 bg-black/30 border border-white/10 rounded-lg p-3">
                        <span className="text-2xl flex-shrink-0">{b.e}</span>
                        <div className="min-w-0">
                          <p className="text-white text-sm font-semibold truncate">{b.n}</p>
                          {b.r ? <p className="text-[#00F0FF] text-xs">{b.r}</p> : <p className="text-zinc-500 text-xs truncate">{b.c}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            {/* Point de rendez-vous : carte Google Maps intégrée */}
            {activity.meeting_point && (
              <Card className="bg-zinc-900/50 border-white/10 p-6 mt-6" data-testid="meeting-point-map">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                  <div className="flex items-start gap-2 min-w-0">
                    <MapPin className="h-5 w-5 text-[#00F0FF] mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <h2 className="text-xl font-bold text-white">{t('activityDetail.meetingPoint')}</h2>
                      <p className="text-zinc-400 text-sm">{activity.meeting_point}</p>
                    </div>
                  </div>
                  <a href={'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(meetingQuery)}
                    target="_blank" rel="noreferrer" className="flex-shrink-0">
                    <Button variant="outline" size="sm" className="border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10 w-full sm:w-auto">
                      <MapPin className="h-4 w-4 mr-1" /> {t('activityDetail.itinerary')}
                    </Button>
                  </a>
                </div>
                <div className="rounded-xl overflow-hidden border border-white/10">
                  <iframe
                    title={t('activityDetail.mapTitle')}
                    src={'https://maps.google.com/maps?q=' + encodeURIComponent(meetingQuery) + '&z=16&output=embed'}
                    className="w-full"
                    style={{ height: '320px', border: 0, filter: 'invert(90%) hue-rotate(180deg)' }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                </div>
              </Card>
            )}

            <Card className="bg-zinc-900/50 border-white/10 p-6 mt-6" data-testid="reviews-section">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white">{t('activityDetail.reviewsTitle')}</h2>
                {totalReviews > 0 && (
                  <div className="flex items-center gap-1 text-[#D4AF37]" data-testid="reviews-average">
                    <Star className="h-5 w-5 fill-[#D4AF37]" />
                    <span className="text-white font-bold">{Number(avgRating).toFixed(1)}</span>
                    <span className="text-zinc-400 text-sm">/ 5 ({totalReviews})</span>
                  </div>
                )}
              </div>

              {reviewableBooking && (
                <div className="bg-zinc-800/50 border border-[#F9004D]/30 rounded-xl p-4 mb-6" data-testid="review-form">
                  <p className="text-white font-bold mb-2">{t('activityDetail.reviewPrompt')}</p>
                  <div className="flex gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map(function(n) {
                      return (
                        <button key={n} type="button" onClick={function() { setMyRating(n); }} data-testid={'star-' + n}>
                          <Star className={'h-7 w-7 ' + (n <= myRating ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-zinc-600')} />
                        </button>
                      );
                    })}
                  </div>
                  <textarea value={myComment} onChange={function(e) { setMyComment(e.target.value); }}
                    placeholder={t('activityDetail.reviewPlaceholder')} rows={3}
                    className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-3 text-white mb-3" data-testid="review-comment" />
                  <Button onClick={submitReview} disabled={submittingReview} className="bg-[#F9004D] hover:bg-[#D00040] text-white" data-testid="submit-review-btn">
                    {submittingReview ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                    {t('activityDetail.publishReview')}
                  </Button>

                  <div className="mt-4 pt-4 border-t border-white/10">
                    <p className="text-white font-bold mb-1">{t('activityDetail.shareVideo')}</p>
                    <p className="text-zinc-400 text-sm">{t('activityDetail.shareVideoDesc')}</p>
                    <VideoUpload activityId={activityId} bookingId={reviewableBooking.booking_id} />
                  </div>
                </div>
              )}

              {reviews.length === 0 ? (
                <p className="text-zinc-400">{t('activityDetail.noReviews')}</p>
              ) : (
                <div className="space-y-4">
                  {reviews.map(function(r) {
                    return (
                      <div key={r.review_id} className="border-b border-white/5 pb-4" data-testid={'review-' + r.review_id}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-white font-bold">{r.traveler_name}</span>
                          <div className="flex gap-0.5">
                            {[1, 2, 3, 4, 5].map(function(n) {
                              return <Star key={n} className={'h-4 w-4 ' + (n <= r.rating ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-zinc-700')} />;
                            })}
                          </div>
                        </div>
                        {r.comment && <p className="text-zinc-300 text-sm">{r.comment}</p>}
                        <p className="text-zinc-600 text-xs mt-1">{r.created_at ? new Date(r.created_at).toLocaleDateString(dateLocale(i18n.language)) : ''}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            <VideoGallery scope="activity" id={activityId} title={t('activityDetail.travelerVideos')} />
          </div>

          <div className="lg:col-span-1">
            <Card className="bg-zinc-900/50 border-white/10 p-6 sticky top-24">
              <h3 className="text-xl font-bold text-white mb-6">{t('activityDetail.prices')}</h3>

              <div className="space-y-4 mb-6">
                {isSej ? (
                  <div className="p-4 bg-zinc-800/50 rounded-lg">
                    <p className="text-zinc-400 text-sm mb-2">{t('activityDetail.packagePrice')}</p>
                    <div className="space-y-1">
                      {pkgBrackets.length > 0 ? pkgBrackets.map(function(b) {
                        return <div key={b.to} className="flex justify-between items-center text-sm">
                          <span className="text-zinc-300">
                            {b.from === b.to ? t('activityDetail.bracketOne', { to: b.to })
                                             : t('activityDetail.bracketRange', { from: b.from, to: b.to })}
                          </span>
                          <span className="text-[#00F0FF] font-bold">{formatYen(b.price, activity.currency)}</span>
                        </div>;
                      }) : <p className="text-zinc-500 text-sm">{t('activityDetail.notDefined')}</p>}
                    </div>
                    <p className="text-zinc-500 text-xs mt-2">{t('activityDetail.packageHint', { count: activity.duration_days || 0 })}</p>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-center p-4 bg-zinc-800/50 rounded-lg">
                      <div>
                        <span className="text-zinc-400 text-sm">{t('activityDetail.adult')}</span>
                        <p className="text-white text-sm">{t('activityDetail.perDay')}</p>
                      </div>
                      <span className="text-2xl font-bold text-[#00F0FF]">
                        {formatYen(activity.adult_rate, activity.currency)}
                      </span>
                    </div>

                    {activity.children_allowed ? (
                      <div className="flex justify-between items-center p-4 bg-zinc-800/50 rounded-lg">
                        <div>
                          <span className="text-zinc-400 text-sm">{t('activityDetail.child')}</span>
                          <p className="text-white text-sm">{t('activityDetail.perDay')}</p>
                        </div>
                        <span className="text-2xl font-bold text-[#D4AF37]">
                          {formatYen(activity.child_rate, activity.currency)}
                        </span>
                      </div>
                    ) : (
                      <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <div className="flex items-center gap-2 text-red-400">
                          <BanIcon className="h-5 w-5" />
                          <span className="font-bold">{t('activityDetail.childrenNotAllowed')}</span>
                        </div>
                        <p className="text-red-400/70 text-sm mt-1">
                          {t('activityDetail.adultsOnly')}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="space-y-3 mb-6 text-sm text-zinc-400">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-[#F9004D]" />
                  <span>{t('activityDetail.maxParticipants', { count: activity.max_participants })}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  <span>{t('activityDetail.securePayment')}</span>
                </div>
              </div>

              {auth.user && auth.user.role === 'guide' ? (
                <div className="w-full space-y-3">
                  <Button
                    onClick={handleDuplicate}
                    className="w-full bg-[#00F0FF] hover:bg-[#00c8d4] text-black py-6 text-lg font-bold rounded-full"
                  >
                    {t('activityDetail.duplicateActivity')}
                  </Button>
                  <p className="text-xs text-zinc-500 text-center">
                    {t('activityDetail.hostDuplicateHint')}
                  </p>
                </div>
              ) : (
                <Button
                  onClick={handleBook}
                  className="w-full bg-[#F9004D] hover:bg-[#D00040] text-white py-6 text-lg font-bold rounded-full"
                >
                  {t('activityDetail.bookNow')}
                  <ChevronRight className="h-5 w-5 ml-2" />
                </Button>
              )}

              <p className="text-xs text-zinc-500 text-center mt-4">
                {t('activityDetail.footerLine')}
              </p>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
